// ─────────────────────────────────────────────────────────────────────────────
// useFilterValidation.js
// A reusable hook that manages per-field errors, scroll-to-first-error, and
// focus-first-error behaviour for all listing-page filter drawers.
//
// Usage
// ─────
//   const { fieldErrors, setFieldErrors, clearAllFieldErrors, scrollToFirstError }
//     = useFilterValidation();
//
//   // Register a ref for a field that can have a validation error:
//   const priceRef = useRef(null);
//
//   // After setting fieldErrors call:
//   scrollToFirstError({ price: priceRef, locality: localityRef, ... });
//
// The hook does NOT run validation itself – that stays in each page component
// exactly as before.  It only owns the error state and the UX side-effects.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback } from "react";

/**
 * @returns {{
 *   fieldErrors: Record<string, string>,
 *   setFieldErrors: Function,
 *   clearAllFieldErrors: Function,
 *   scrollToFirstError: (fieldRefMap: Record<string, React.RefObject>) => void
 * }}
 */
export function useFilterValidation() {
  const [fieldErrors, setFieldErrors] = useState({});

  const clearAllFieldErrors = useCallback(() => setFieldErrors({}), []);

  /**
   * Given a map of { errorKey → React ref }, find the first key that has an
   * error and:
   *   1. Smooth-scroll the ref's element into view inside the modal/drawer.
   *   2. Focus the element if it is focusable.
   *
   * @param {Record<string, React.RefObject>} fieldRefMap
   * @param {Record<string, string>}          errors  – the errors object just produced
   */
  const scrollToFirstError = useCallback((fieldRefMap, errors) => {
    // fieldRefMap keys should be in the DOM order you want to scroll to first.
    const firstErrorKey = Object.keys(fieldRefMap).find((key) => errors[key]);
    if (!firstErrorKey) return;

    const ref = fieldRefMap[firstErrorKey];
    const el = ref?.current;
    if (!el) return;

    // Use scrollIntoView with block:"nearest" so it works inside modals too.
    // A small rAF delay lets the React render (error state) complete first.
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });

      // Focus: prefer the element itself, or the first focusable child.
      const focusable = el.matches(
        "input, select, textarea, button, [tabindex]"
      )
        ? el
        : el.querySelector("input, select, textarea, button, [tabindex]");

      if (focusable) {
        focusable.focus({ preventScroll: true });
      }
    });
  }, []);

  return { fieldErrors, setFieldErrors, clearAllFieldErrors, scrollToFirstError };
}